import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

// ═══ 复购提醒 ═══
function ReplenishView() {
  const [models, setModels] = useState<any[]>([]);
  const [reminders, setReminders] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/replenish/models?limit=50').then((r: any) => setModels(r.data || [])).catch(() => {});
    get('/api/v1/replenish/reminders').then((r: any) => setReminders(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<any>, msg: (d: any) => string) => {
    setBusy(true);
    try { const r: any = await fn(); alert(msg(r.data)); load(); } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={() => run(() => post('/api/v1/replenish/analyze'), d => `分析 ${d.orders_analyzed} 订单，建立 ${d.models_built} 个消耗模型`)}>
          🔄 分析订单建模
        </button>
        <button style={S.btnPrimary} disabled={busy} onClick={() => run(() => post('/api/v1/replenish/schedule', {}), d => `到期 ${d.due_models} 个，生成 ${d.reminders_created} 条提醒`)}>
          📧 生成本周复购提醒
        </button>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('消耗周期模型（按到期排序）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('周期(天)')}</th><th style={S.th}>{t('购买次数')}</th><th style={S.th}>{t('下次到期')}</th><th style={S.th}>{t('置信度')}</th></tr></thead>
          <tbody>
            {models.map((m: any) => (
              <tr key={`${m.customer_id}-${m.product_id}`}>
                <td style={S.td}>{m.customer_email || m.customer_id}</td>
                <td style={S.td}>{m.product_title}</td>
                <td style={S.td}>{m.avg_interval_days}</td>
                <td style={S.td}>{m.purchase_count}</td>
                <td style={S.td}>{(m.next_due_at || '').slice(0, 10)}</td>
                <td style={S.td}>{Math.round((m.confidence || 0) * 100)}%</td>
              </tr>
            ))}
            {models.length === 0 && <tr><td style={S.td} colSpan={6}>{t('暂无模型 — 点击"分析订单建模"')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('复购提醒队列')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('计划时间')}</th><th style={S.th}>{t('文案')}</th><th style={S.th}>{t('状态')}</th></tr></thead>
          <tbody>
            {reminders.map((r: any) => (
              <tr key={r.id}>
                <td style={S.td}>{r.customer_email || r.customer_id}</td>
                <td style={S.td}>{r.product_title}</td>
                <td style={S.td}>{(r.scheduled_for || '').slice(0, 10)}</td>
                <td style={{ ...S.td, maxWidth: 320 }}>{r.message}</td>
                <td style={S.td}>{r.status}</td>
              </tr>
            ))}
            {reminders.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无提醒')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 库存预警 ═══
function InventoryView() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/inventory/alerts?status=all').then((r: any) => setAlerts(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/inventory/scan', {}); alert(`扫描 ${r.data.products_scanned} 商品，新增 ${r.data.new_alerts} 条预警`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const startRfq = async (a: any) => {
    const qty = prompt(t('补货数量？'), String(Math.max(50, Math.ceil((a.daily_velocity || 1) * 60))));
    if (!qty) return;
    try {
      const r: any = await post('/api/v1/rfqs', { alert_id: a.id, items: [{ sku: a.variant_id, title: a.title, quantity: Number(qty) }] });
      alert(`已为 ${r.data.suppliers} 家供应商生成询价草稿`);
      load();
    } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15 }}>{t('库存预警')}</h3>
        <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🔍 扫描库存水位')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('库存')}</th><th style={S.th}>{t('日动销')}</th><th style={S.th}>{t('剩余天数')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
        <tbody>
          {alerts.map((a: any) => (
            <tr key={a.id}>
              <td style={S.td}>{a.title}</td>
              <td style={S.td}>{a.current_stock}</td>
              <td style={S.td}>{a.daily_velocity}</td>
              <td style={{ ...S.td, color: a.days_remaining < 7 ? '#d93025' : '#e8710a', fontWeight: 600 }}>{a.days_remaining}天</td>
              <td style={S.td}>{a.status}</td>
              <td style={S.td}>
                {a.status === 'open' && <button style={S.btnPrimary} onClick={() => startRfq(a)}>{t('发起询价')}</button>}
              </td>
            </tr>
          ))}
          {alerts.length === 0 && <tr><td style={S.td} colSpan={6}>{t('暂无预警 — 点击"扫描库存水位"')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 供应商与询价 ═══
function SuppliersView() {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [rfqs, setRfqs] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [form, setForm] = useState({ name: '', email: '', lead_time_days: 14 });

  const load = useCallback(() => {
    get('/api/v1/suppliers').then((r: any) => setSuppliers(r.data || [])).catch(() => {});
    get('/api/v1/rfqs').then((r: any) => setRfqs(r.data || [])).catch(() => {});
    get('/api/v1/pos').then((r: any) => setPos(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const addSupplier = async () => {
    if (!form.name || !form.email) return alert('请填写名称和邮箱');
    try { await post('/api/v1/suppliers', form); setForm({ name: '', email: '', lead_time_days: 14 }); load(); }
    catch (e: any) { alert(e.message); }
  };

  const pasteReply = async (id: string) => {
    const text = prompt('粘贴供应商回复邮件内容：');
    if (!text) return;
    try { const r: any = await post(`/api/v1/rfqs/${id}/reply`, { reply_text: text }); alert(`解析结果：$${r.data.total_price ?? '?t('}，交期 ${r.data.lead_days ?? ')?t('} 天，MOQ ${r.data.moq ?? ')?'}`); load(); }
    catch (e: any) { alert(e.message); }
  };

  const makePO = async (rfqId: string) => {
    try { await post('/api/v1/pos', { rfq_id: rfqId }); alert('采购单草稿已生成'); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>供应商（{suppliers.length}）</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={S.input} placeholder={t('名称')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <input style={S.input} placeholder={t('邮箱')} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input style={{ ...S.input, width: 90 }} type="number" title={t('交期天数')} value={form.lead_time_days} onChange={e => setForm({ ...form, lead_time_days: Number(e.target.value) })} />
          <button style={S.btnPrimary} onClick={addSupplier}>{t('添加')}</button>
        </div>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('名称')}</th><th style={S.th}>{t('邮箱')}</th><th style={S.th}>{t('默认交期')}</th><th style={S.th}></th></tr></thead>
          <tbody>
            {suppliers.map((s: any) => (
              <tr key={s.id}>
                <td style={S.td}>{s.name}</td><td style={S.td}>{s.email}</td><td style={S.td}>{s.lead_time_days}天</td>
                <td style={S.td}><button style={S.btn} onClick={async () => { await del(`/api/v1/suppliers/${s.id}`); load(); }}>{t('移除')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('询价单（RFQ）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('供应商')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('报价')}</th><th style={S.th}>{t('交期')}</th><th style={S.th}>MOQ</th><th style={S.th}>{t('评分')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {rfqs.map((r: any) => (
              <tr key={r.id}>
                <td style={S.td}>{r.supplier_name}</td>
                <td style={S.td}>{r.status}</td>
                <td style={S.td}>{r.parsed_price ? `$${r.parsed_price}` : '-'}</td>
                <td style={S.td}>{r.parsed_lead_days ? `${r.parsed_lead_days}天` : '-'}</td>
                <td style={S.td}>{r.parsed_moq ?? '-'}</td>
                <td style={S.td}>{r.score ?? '-'}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  {r.status === 'draft' && <button style={S.btn} onClick={async () => { await post(`/api/v1/rfqs/${r.id}/mark-sent`); load(); }}>{t('标记已发')}</button>}
                  {r.status === 'sent' && <button style={S.btn} onClick={() => pasteReply(r.id)}>{t('粘贴回复')}</button>}
                  {r.status === 'replied' && <button style={S.btnPrimary} onClick={() => makePO(r.id)}>{t('生成采购单')}</button>}
                </td>
              </tr>
            ))}
            {rfqs.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无询价单 — 从库存预警发起')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('采购单')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('供应商')}</th><th style={S.th}>{t('金额')}</th><th style={S.th}>{t('预计到货')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {pos.map((p: any) => (
              <tr key={p.id}>
                <td style={S.td}>{p.supplier_name}</td>
                <td style={S.td}>{p.total_amount ? `$${p.total_amount}` : '-'}</td>
                <td style={S.td}>{p.expected_delivery || '-'}</td>
                <td style={S.td}>{p.status}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  {p.status === 'draft' && <button style={S.btnPrimary} onClick={async () => { await post(`/api/v1/pos/${p.id}/confirm`); load(); }}>{t('确认')}</button>}
                  {p.status === 'confirmed' && <button style={S.btn} onClick={async () => { await post(`/api/v1/pos/${p.id}/receive`); load(); }}>{t('到货')}</button>}
                </td>
              </tr>
            ))}
            {pos.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无采购单')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('replenish');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>SmartReplenish</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('智能补货与供应链自主优化 — 复购预测 · 库存预警 · 自动询价')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'replenish')} onClick={() => setTab('replenish')}>{t('复购引擎')}</div>
        <div style={S.tab(tab === 'inventory')} onClick={() => setTab('inventory')}>{t('库存预警')}</div>
        <div style={S.tab(tab === 'suppliers')} onClick={() => setTab('suppliers')}>{t('供应商与采购')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'replenish' && <ReplenishView />}
      {tab === 'inventory' && <InventoryView />}
      {tab === 'suppliers' && <SuppliersView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
